'use client';

import type { ReactNode } from 'react';
import type { ManualId } from 'src/types/manual/manual-domain';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { ManualGuideLink } from 'src/components/cmui/manual-guide-link';

import { usePageHeading } from './page-heading-context';

const HEAD_ROW_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', sm: 'minmax(0, 1fr) auto auto' },
  gridTemplateAreas: { xs: '"head guide" "side side"', sm: '"head side guide"' },
  alignItems: 'end',
  columnGap: 1.5,
  rowGap: 1,
} as const;

/** 「사용 가이드」가 없으면 옆 칸은 폭과 상관없이 제목 줄에 남는다 */
const HEAD_ROW_NO_GUIDE_SX = {
  ...HEAD_ROW_SX,
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gridTemplateAreas: '"head side"',
} as const;

function HeadingNames({ section, text }: { section?: string | null; text: string }) {
  return (
    <Box data-capture="heading" sx={{ minWidth: 0 }}>
      {section && (
        <Typography
          component="div"
          variant="uiLabel"
          sx={{ display: 'block', color: 'primary.main', textTransform: 'uppercase' }}
        >
          {section}
        </Typography>
      )}
      <Typography component="h1" variant="uiHead" sx={{ display: 'block' }}>
        {text}
      </Typography>
    </Box>
  );
}

/**
 * 서비스 화면의 **머리 두 줄** — 위는 사이드바 구역 이름(작은 파란 글자), 아래는 메뉴 이름(화면 제목).
 * 「사용 가이드」의 자리는 이 컴포넌트 하나가 정한다 — 화면이 `ManualGuideLink` 를 따로 놓지 않는다.
 *
 * 🔑 두 이름 모두 사이드바가 쥔 값이다(`usePageHeading`) — 화면에서 이름을 박지 않는다.
 * ⚠️ 구역 이름은 사이드바 표제처럼 **대문자로 보인다**(값은 `Discover` 그대로, 모양만 바꾼다).
 * ⛔ 제목 아래 설명 줄을 두지 않는다 — 화면이 무엇인지는 제목과 본문이 말한다.
 * 🔑 「사용 가이드」는 폭과 상관없이 **제목 줄 오른쪽 끝**이다. `side` 는 넓은 화면에서 제목과 가이드 사이에,
 *   폰에서는 아래 줄 오른쪽에 선다(가이드까지 한 줄에 두면 제목이 낱말마다 꺾인다) — 가이드가 없으면 폰에서도
 *   제목 줄에 남는다. 둘 다 제목 줄에 아래 맞춤이다.
 * @param title 메뉴 이름 대신 세울 제목 — 공유 페이지처럼 사이드바가 없는 자리, 고른 세트 이름 등
 * @param guide 이 화면의 매뉴얼 id — 주면 「사용 가이드」가 선다(`ManualGuideLink`)
 * @param side 머리 오른쪽의 주요 단추·이용 횟수 같은 것
 */
export function PageHeading({ title, guide, side }: { title?: string; guide?: ManualId; side?: ReactNode }) {
  const heading = usePageHeading();
  const text = title || heading?.title;
  if (!text) return null;

  const names = <HeadingNames section={heading?.section} text={text} />;
  if (!guide && !side) return names;

  return (
    <Box sx={guide ? HEAD_ROW_SX : HEAD_ROW_NO_GUIDE_SX}>
      <Box sx={{ gridArea: 'head', minWidth: 0 }}>{names}</Box>
      {side && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ gridArea: 'side', justifySelf: 'end' }}>
          {side}
        </Stack>
      )}
      {guide && <ManualGuideLink id={guide} sx={{ gridArea: 'guide' }} />}
    </Box>
  );
}
